import React, { useState, useMemo } from 'react'
import { Modal, Form, Input, Select, InputNumber } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { merchantsService } from '../../../shared/api/services/merchants.service'
import { trialsService } from '../services/trials.service'
import debounce from 'lodash/debounce'

interface TrialAssignmentModalProps {
    open: boolean
    onCancel: () => void
    onSubmit: (values: any) => void
}

export const TrialAssignmentModal: React.FC<TrialAssignmentModalProps> = ({
    open,
    onCancel,
    onSubmit
}) => {
    const [form] = Form.useForm()
    const [searchText, setSearchText] = useState('')

    // Fetch merchants
    const { data: merchantsData, isLoading: isLoadingMerchants } = useQuery({
        queryKey: ['merchants', searchText],
        queryFn: () => merchantsService.getMerchants({
            page: 1,
            per_page: 50,
            search: searchText || undefined,
            status: 'active' // Only active merchants should get trials? Assuming yes.
        }),
        enabled: open // Only fetch when modal is open
    })

    // Fetch all active trials to exclude
    const { data: trialsData, isLoading: isLoadingTrials } = useQuery({
        queryKey: ['trials', 'active-for-assignment'],
        queryFn: () => trialsService.getTrials(1, 1000, 'active'), // Fetch enough to cover
        enabled: open
    })

    // Filter logic
    const eligibleMerchants = useMemo(() => {
        if (!merchantsData?.data || !trialsData?.data) return merchantsData?.data || []

        // Create a set of merchant IDs that already have active trials
        // Note: trialsData.data is BackendTrial[], merchantsData.data is Merchant[]
        // We need to ensure ID comparison is correct (string vs number/string)
        const activeMerchantIds = new Set(trialsData.data.map(t => String(t.merchant_id)))

        return merchantsData.data.filter(m => !activeMerchantIds.has(String(m.merchant_id)))
    }, [merchantsData, trialsData])

    // Debounce search
    const handleSearch = useMemo(() => debounce((value: string) => {
        setSearchText(value)
    }, 500), [])

    const handleOk = () => {
        form.validateFields().then(values => {
            onSubmit(values)
            form.resetFields()
        })
    }

    return (
        <Modal
            title="Assign Trial to Merchant"
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Select Merchant"
                    name="merchantId"
                    rules={[{ required: true, message: 'Please select a merchant' }]}
                    help="Only merchants without active trials are shown"
                >
                    <Select
                        placeholder="Search or select merchant"
                        showSearch
                        onSearch={handleSearch}
                        filterOption={false} // Server-side filtering via searchText (filtered results then filtered by trial check)
                        loading={isLoadingMerchants || isLoadingTrials}
                        notFoundContent={isLoadingMerchants ? 'Loading...' : 'No eligible merchants found'}
                    >
                        {eligibleMerchants.map(merchant => (
                            <Select.Option key={merchant.merchant_id} value={merchant.merchant_id}>
                                {merchant.business_name} ({merchant.email})
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label="Trial Duration (Days)"
                    name="duration"
                    rules={[{ required: true, message: 'Please enter duration' }]}
                    initialValue={14}
                >
                    <InputNumber min={1} max={90} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    label="Trial Bidding Radius (KM)"
                    name="trial_bidding_radius_km"
                    initialValue={10}
                    tooltip="Merchants within this radius can see and bid on RFQs during their trial"
                    rules={[{ required: true, message: 'Please enter a bidding radius' }]}
                >
                    <InputNumber min={1} max={50} step={0.5} addonAfter="KM" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    label="Trial Visibility Radius (KM)"
                    name="trial_visibility_radius_km"
                    initialValue={15}
                    tooltip="Merchants within this radius (but beyond bidding radius) can see RFQs but cannot bid"
                    rules={[{ required: true, message: 'Please enter a visibility radius' }]}
                >
                    <InputNumber min={1} max={100} step={0.5} addonAfter="KM" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    label="Reason (Optional)"
                    name="reason"
                >
                    <Input.TextArea rows={2} placeholder="e.g. New user promotion" />
                </Form.Item>
            </Form>
        </Modal>
    )
}
